import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { NavLink, useNavigate } from 'react-router-dom';
import { AUTH_SESSION } from '@prosaas/psw-events';
import {
  Alert,
  FormFieldTextInput,
  CardTitle,
  CardBodyWrapper,
  ColumnWrapper,
  Button,
  Card,
  FullPageSpinner,
  NavLinkDisplay,
  Spacer,
  IconAtSymbol,
} from '@prosaas/psw-styleguide';
import { skipEmptyPayload, subscribe } from '@prosaas/psw-utils';
import { apiService } from '../services/api.service';
import { EMAIL_REGEX } from '../utils/regexes';
import { homeRoute } from '../utils/routes';
import { authTestIds } from '../testIds.js';
import { useAuthTranslations } from '../hooks/useAuthTranslations';

type FormInputs = {
  email: string;
};

const ForgotPasswordForm: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isSending, setIsSending] = useState(false);
  const [apiError, setApiErr] = useState<false | { [key: string]: any }>(undefined);
  const [linkIsEmailed, setLinkIsEmailed] = useState(false);
  const navigate = useNavigate();

  const [translations, isLoadingTranslations] = useAuthTranslations();

  useEffect(() => {
    const unsubscriber = subscribe(
      AUTH_SESSION,
      skipEmptyPayload(({ isLoggedIn }) => {
        setIsLoggedIn(isLoggedIn);
      }),
    );

    return function cleanUp() {
      unsubscriber();
    };
  }, []);

  const {
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>();
  const email = watch('email');

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    setApiErr(false);
    setIsSending(true);

    try {
      await apiService.forgottenPassword(data.email);
      setLinkIsEmailed(true);
    } catch (error) {
      setApiErr(error);
    } finally {
      setIsSending(false);
    }
  };

  if (isLoggedIn) {
    navigate(homeRoute());
    return null;
  }

  const HEADER_EMAIL_SENT = translations.headerEmailSent();
  const CLICK_LINK_IN_EMAIL = translations.clickLinkInEmail();
  const NOT_SENT = translations.notSent();
  const HEADER_RESET_LINK = translations.headerGetResetLink();
  const ENTER_YOUR_EMAIL = translations.enterYourEmail();
  const EMAIL_ADDRESS = translations.emailAddress();
  const INVALID_EMAIL = translations.invalidEmail();
  const SUBMIT_BUTTON = isSending ? translations.submitting() : translations.submit();
  const SIGN_IN_INSTEAD = translations.signInInstead();

  if (isLoadingTranslations) {
    return <FullPageSpinner />;
  }

  if (linkIsEmailed) {
    return (
      <Card isRaised>
        <CardTitle>{HEADER_EMAIL_SENT}</CardTitle>
        <p>{CLICK_LINK_IN_EMAIL}</p>
        <p>{email}</p>
        <Spacer />
        <NavLinkDisplay
          displayName={NOT_SENT}
          onClick={() => {
            setLinkIsEmailed(false);
          }}
        />
      </Card>
    );
  }

  return (
    <Card isRaised>
      <ColumnWrapper>
        <CardBodyWrapper>
          {isSending && <FullPageSpinner />}
          <CardTitle>{HEADER_RESET_LINK}</CardTitle>
          {apiError && <Alert category="error">{apiError?.message || 'Something went wrong'}</Alert>}
          <ColumnWrapper gapSize="sm">
            <Controller
              defaultValue=""
              control={control}
              name={'email'}
              rules={{
                required: ENTER_YOUR_EMAIL,
                pattern: {
                  value: EMAIL_REGEX,
                  message: INVALID_EMAIL,
                },
              }}
              render={({ field: { value, onChange } }) => (
                <FormFieldTextInput
                  placeholder={ENTER_YOUR_EMAIL}
                  label={EMAIL_ADDRESS}
                  startIcon={<IconAtSymbol />}
                  disabled={isSending}
                  error={errors?.email?.message || null}
                  value={value}
                  onChange={onChange}
                  testId={authTestIds.loginEmail}
                />
              )}
            />
            <Button
              disabled={!!errors.email || isSending}
              onClick={handleSubmit(onSubmit)}
              data-test-id={authTestIds.getResetLinkButton}
            >
              {SUBMIT_BUTTON}
            </Button>
          </ColumnWrapper>
        </CardBodyWrapper>
        <NavLink data-test-id={authTestIds.signInInstead} to="/">
          <NavLinkDisplay displayName={SIGN_IN_INSTEAD} />
        </NavLink>
      </ColumnWrapper>
    </Card>
  );
};

export default ForgotPasswordForm;
